/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import ApiService from "./ApiService";
import Spell from '../model/Spell';
import ClassService from "./ClassService";
import Class from "../model/Class";
import Section from "../model/Section";
import { ClassDetails } from "../model/ClassDetails";
import Attributes from "../model/Attributes";

class SpellService {

    static parseSpells(spells: any[]): Map<string, Map<number, Spell[]>> {
        const spellsBySpellLevelByClass = new Map<string, Map<number, Spell[]>>();
        for (const spell of spells) {
            // There are cases where the dnd_class has more than the spell_lists
            // such as the "Alarm" spell:
            //    "dnd_class": "Ranger, Ritual Caster, Wizard",
            //    "spell_lists": ["ranger","wizard"],
            // and the "Aid" spell:
            //    "dnd_class": "Cleric, Herald",
            //    "spell_lists": [],
            const classes = spell.dnd_class.toLowerCase().split(/, ?/);
            const classesSet = new Set<string>();
            for (const clss of classes) {
                classesSet.add(clss);
            }
            for (const clss of spell.spell_lists || []) {
                classesSet.add(clss);
            }
            classesSet.add('inate caster - cha'); // Meta Casters can cast everything!
            classesSet.add('inate caster - int'); // Meta Casters can cast everything!
            classesSet.add('inate caster - wis'); // Meta Casters can cast everything!

            for (const clss of classesSet) {
                let spellsBySpellLevelForClass = spellsBySpellLevelByClass.get(clss);
                if (!spellsBySpellLevelForClass) {
                    spellsBySpellLevelByClass.set(clss, spellsBySpellLevelForClass = new Map<number, Spell[]>());
                }
                let spellsForLevelAndClass = spellsBySpellLevelForClass.get(spell.spell_level);
                if (!spellsForLevelAndClass) {
                    spellsBySpellLevelForClass.set(spell.spell_level, spellsForLevelAndClass = []);
                }
                spellsForLevelAndClass.push(spell);
            }
        }
        spellsBySpellLevelByClass.set('soulspy',            spellsBySpellLevelByClass.get('cleric') || new Map());
        spellsBySpellLevelByClass.set('underfoot',          spellsBySpellLevelByClass.get('druid')  || new Map());
        spellsBySpellLevelByClass.set('eldritch trickster', spellsBySpellLevelByClass.get('wizard') || new Map());
        spellsBySpellLevelByClass.set('arcane warrior',     spellsBySpellLevelByClass.get('wizard') || new Map());
        const smugglerSpells = new Map();
        const wizardSpells = spellsBySpellLevelByClass.get('wizard') || new Map();
        for (let spellLevel = 0; spellLevel <= 2; spellLevel++) {
            const wizAtLevel: Spell[] = wizardSpells.get(spellLevel);
            if (wizAtLevel) {
                let smugglerAtLevel = wizAtLevel.filter((spell: Spell) => spell.school.toLowerCase().includes('illusion') ||
                                                                          spell.school.toLowerCase().includes('transmutation'));
                if (spellLevel == 2) {
                    const allowedSpellNames = ["arcanist-s magic aura", "blur", "darkvision", "enlarge/reduce", "invisibility",
                                               "knock", "levitate", "magic mouth", "mirror image", "rope trick", "spider climb"]
                    smugglerAtLevel = smugglerAtLevel.filter((spell: Spell) => allowedSpellNames.includes(spell.name.toLowerCase()));
                }
                smugglerSpells.set(spellLevel, smugglerAtLevel);
            }
        }
        spellsBySpellLevelByClass.set('smuggler', smugglerSpells);
        return spellsBySpellLevelByClass;
    };

    static spellSplotsArrayBySpellLevel = [[],                             // 0
          /* First column is cantrips,   */[0, 2, 0, 0, 0, 0, 0, 0, 0, 0], // 1
          /* which is defined            */[0, 3, 0, 0, 0, 0, 0, 0, 0, 0], // 2
          /* specifically by each class. */[0, 4, 2, 0, 0, 0, 0, 0, 0, 0], // 3
                                           [0, 4, 3, 0, 0, 0, 0, 0, 0, 0], // 4
                                           [0, 4, 3, 2, 0, 0, 0, 0, 0, 0], // 5
                                           [0, 4, 3, 3, 0, 0, 0, 0, 0, 0], // 6
                                           [0, 4, 3, 3, 1, 0, 0, 0, 0, 0], // 7
                                           [0, 4, 3, 3, 2, 0, 0, 0, 0, 0], // 8
                                           [0, 4, 3, 3, 3, 1, 0, 0, 0, 0], // 9
                                           [0, 4, 3, 3, 3, 2, 0, 0, 0, 0], // 10
                                           [0, 4, 3, 3, 3, 2, 1, 0, 0, 0], // 11
                                           [0, 4, 3, 3, 3, 2, 1, 0, 0, 0], // 12
                                           [0, 4, 3, 3, 3, 2, 1, 1, 0, 0], // 13
                                           [0, 4, 3, 3, 3, 2, 1, 1, 0, 0], // 14
                                           [0, 4, 3, 3, 3, 2, 1, 1, 1, 0], // 15
                                           [0, 4, 3, 3, 3, 2, 1, 1, 1, 0], // 16
                                           [0, 4, 3, 3, 3, 2, 1, 1, 1, 1], // 17
                                           [0, 4, 3, 3, 3, 3, 1, 1, 1, 1], // 18
                                           [0, 4, 3, 3, 3, 3, 2, 1, 1, 1], // 19
                                           [0, 4, 3, 3, 3, 3, 2, 2, 1, 1], // 20
                                           [0, 4, 3, 3, 3, 3, 2, 2, 1, 1]  // 21
                                          ];

    static spellbookSpellsKnowableByWizardLevel = [[],                             // 0
                                                   [3, 6, 0, 0, 0, 0, 0, 0, 0, 0], // 1
                                                   [3, 8, 0, 0, 0, 0, 0, 0, 0, 0], // 2
                                                   [3, 8, 2, 0, 0, 0, 0, 0, 0, 0], // 3
                                                   [4, 8, 4, 0, 0, 0, 0, 0, 0, 0], // 4
                                                   [4, 8, 4, 2, 0, 0, 0, 0, 0, 0], // 5
                                                   [4, 8, 4, 4, 0, 0, 0, 0, 0, 0], // 6
                                                   [4, 8, 4, 4, 2, 0, 0, 0, 0, 0], // 7
                                                   [4, 8, 4, 4, 4, 0, 0, 0, 0, 0], // 8
                                                   [4, 8, 4, 4, 4, 2, 0, 0, 0, 0], // 9
                                                   [5, 8, 4, 4, 4, 4, 0, 0, 0, 0], // 10
                                                   [5, 8, 4, 4, 4, 4, 2, 0, 0, 0], // 11
                                                   [5, 8, 4, 4, 4, 4, 4, 0, 0, 0], // 12
                                                   [5, 8, 4, 4, 4, 4, 4, 2, 0, 0], // 13
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 0, 0], // 14
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 2, 0], // 15
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 4, 0], // 16
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 4, 2], // 17
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 4, 4], // 18
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 4, 6], // 19
                                                   [5, 8, 4, 4, 4, 4, 4, 4, 4, 8], // 20
                                                   [9, 9, 9, 9, 9, 9, 9, 9, 9, 9]  // 21
                                                  ];

    static levelByClass(classInfo: ClassDetails | null): number {
        const className = classInfo?.classInfo?.name || null;
        const archetypeName = classInfo?.archetype?.name || null;
        const classLevel = classInfo?.classLevel || 0;
        /*
            You determine your available spell slots by adding together:
                all your levels in the bard, cleric, druid, sorcerer, and wizard classes,
                half your levels (rounded down) in the paladin and ranger classes,
                half your levels (rounded up) in the artificer class,
                and a third of your fighter or rogue levels (rounded down) if you have the Eldritch Knight or the Arcane Trickster feature.
            Use this total to determine your spell slots by consulting the Multiclass Spellcaster table.
        */
        switch (className?.toLowerCase()) {
            case 'bard':
            case 'cleric':
            case 'druid':
            case 'sorcerer':
            case 'wizard':
            case 'meta caster':
            case 'inate caster - cha':
            case 'inate caster - int':
            case 'inate caster - wis':
                return classLevel;

            case 'paladin':
            case 'ranger':
                if (classLevel == 1) {
                    return 0;
                }
                return classLevel / 2;

            case 'articifer':
                return Math.ceil(classLevel / 2);

            case 'fighter':
            case 'rogue':
                if ((archetypeName?.toLowerCase() === 'arcane warrior') ||
                    (archetypeName?.toLowerCase() === 'eldritch trickster') ||
                    (archetypeName?.toLowerCase() === 'soulspy') ||
                    (archetypeName?.toLowerCase() === 'underfoot')) {
                    return classLevel / 3;
                }
                if (archetypeName?.toLowerCase() === 'smuggler' && classLevel >= 3) {
                    // This is somewhat problematic, it shouldn't really add any levels,
                    // but it must return non zero to be known as a casting class.
                    return 1;
                }
                return 0;
            case 'arcane warrior':
            case 'eldritch trickster':
            case 'soulspy':
            case 'underfoot':
                return classLevel / 3;
            case 'smuggler':
                // This is somewhat problematic, it shouldn't really add any levels,
                // but it must return non zero to be known as a casting class.
                return classLevel > 3 ? 1 : 0;
        }
        return 0;
    };

    static getWarlockSpellSlots(classInfo: Class | null, classLevel: number): number[] | null {
        if (classInfo?.name.toLowerCase() === 'warlock') {
            const slotLevelStr: string = ClassService.getLevelAbility(classInfo, classLevel, 'Slot Level');
            if (slotLevelStr) {
                const slotLevel = parseInt(slotLevelStr);
                const spellSlots = ClassService.getLevelAbility(classInfo, classLevel, 'Spell Slots');
                const slots = Array(10).fill(0); // This fills 0..9, with 0 being cantrips
                slots[slotLevel] = parseInt(spellSlots);
                return slots;
            }
        }
        return null;
    }

    static getTotalSpellLevel(primaryClass: ClassDetails | null, secondaryClass: ClassDetails | null): number {
        const primarySpellCastingLevel = SpellService.levelByClass(primaryClass);
        const secondarySpellCastingLevel = SpellService.levelByClass(secondaryClass);
        // add the levels together before we do any rounding:
        let totalLevels = primarySpellCastingLevel + secondarySpellCastingLevel;
        if (primarySpellCastingLevel > 0 && secondarySpellCastingLevel > 0) {
            // If we have more than one class with spell casting ability, we need to round down:
            return Math.floor(totalLevels);
        }
        // We only have one class with spell casting ability, or none, we need to round up:
        return Math.ceil(totalLevels);
    }

    static getSpellSlots(primaryClass: ClassDetails | null, secondaryClass: ClassDetails | null): number[] {
        let totalLevels = SpellService.getTotalSpellLevel(primaryClass, secondaryClass);
        return SpellService.spellSplotsArrayBySpellLevel[totalLevels];
    }

    static spellsCache: Spell[] = [];
    static async fetchSpells(): Promise<Spell[]> {
        if (!SpellService.spellsCache?.length) {
            SpellService.spellsCache = await ApiService.getAllSpells(false);
        }
        return SpellService.spellsCache;
    }

    static getSpellBySlug(slug: string): Spell | undefined {
        return this.spellsCache?.filter(spell => spell.slug == slug)?.pop();
    }

    static async fetchSpellsBySpellLevelByClass(): Promise<Map<string, Map<number, Spell[]>>> {
        try {
            const spells = await SpellService.fetchSpells();
            return SpellService.parseSpells(spells);
        } catch (error) {
            console.error("Failed to fetch spells", error);
            return new Map();
        }
    }

    static getSpellName = (spell: Spell): string => {
        if (spell.ritual?.toLowerCase() === 'yes') {
            return spell.name + '\u00A0(ᚱ)';
        }
        return spell.name;
    }

    static addSpellBook = (sections: Section[], classInfo: Class | null, spellbookSpellsArrayBySpellLevel: Spell[][]) => {
        if (classInfo == null || classInfo.name !== 'Wizard') {
            return;
        }
        const knownSpellsTableRows: string[] = [];
        for (let spellLevel = 1; spellLevel <= 9; spellLevel++) {
            const spellsSelectedArray = spellbookSpellsArrayBySpellLevel[spellLevel]?.filter(o => o)
            const levelString = ClassService.getLevelString(spellLevel);
            if (spellsSelectedArray?.length) {
                const spells = spellsSelectedArray.map(o => SpellService.getSpellName(o)).join(', ');
                knownSpellsTableRows.push(`| ${levelString} | ${spells} |`);
            }
        }
        if (knownSpellsTableRows.length) {
            const desc = '\n| Level | Spells |\n|----|----|\n' + knownSpellsTableRows.join('\n');
            sections.push(new Section(`Wizard's Spellbook`, desc));
        }
    }

    static addSpells = (sections: Section[],
                        classDetails1: ClassDetails | null, classDetails2: ClassDetails | null,
                        spellSlots: number[], warlockSpellSlots: number[] | null) => {
        classDetails1 && SpellService.addSpellBook(sections, classDetails1.classInfo, classDetails1.spellbookSpellsArrayBySpellLevel);
        classDetails2 && SpellService.addSpellBook(sections, classDetails2.classInfo, classDetails2.spellbookSpellsArrayBySpellLevel);

        const columnLevel: string[] = [];
        const columnSlot: string[] = [];
        const columnClass1: string[] = [];
        const columnClass2: string[] = [];
        let column1Present = false;
        let column2Present = false;
        let warlockPresent = false;
        for (let spellLevel = 0; spellLevel <= 9; spellLevel++) {
            const levelString = spellLevel == 0 ? 'Cantrip' : ClassService.getLevelString(spellLevel);
            columnLevel.push(levelString);
            let slots = '';
            if (spellLevel === 0) {
                slots = '--';
            } else {
                slots += spellSlots[spellLevel] || '';
                if (warlockSpellSlots && warlockSpellSlots[spellLevel] > 0) {
                    if (spellSlots[spellLevel] > 0) {
                        slots = (slots ? (slots + "/") : '') + warlockSpellSlots[spellLevel] + "*";
                    } else {
                        slots = warlockSpellSlots[spellLevel] + '*';
                    }
                    warlockPresent = true;
                }
            }
            columnSlot.push(slots || '');
            const getSpells = (spellsArray: Spell[]) => {
                // remove empty elements
                spellsArray = spellsArray?.filter(o => o);
                if (spellsArray?.length) {
                    return spellsArray.map(o => SpellService.getSpellName(o)).join(', ');
                }
                return '';
            }
            const column1 = classDetails1 ? getSpells(classDetails1.spellsPreparedArrayBySpellLevel[spellLevel]) : '';
            const column2 = classDetails2 ? getSpells(classDetails2.spellsPreparedArrayBySpellLevel[spellLevel]) : '';
            columnClass1.push(column1);
            columnClass2.push(column2);
            if (column1) column1Present = true;
            if (column2) column2Present = true;
        }
        if (!column1Present && !column2Present) {
            return;
        }

        const table: string[][] = [];
        let writeAll = false;
        for (let spellLevel = 9; spellLevel >= 0; spellLevel--) {
            const row: string[] = [];
            row.push(columnLevel[spellLevel]);
            row.push(columnSlot[spellLevel]);
            if (column1Present) { row.push(columnClass1[spellLevel]); }
            if (column2Present) { row.push(columnClass2[spellLevel]); }
            if (writeAll || (column1Present && columnClass1[spellLevel]) ||
                            (column2Present && columnClass2[spellLevel]) ||
                            (columnSlot[spellLevel] && columnSlot[spellLevel] !== '0')) {
                writeAll = true;
                table.unshift(row);
            }
        }
        const separatorRow = ['---', '---', '---'];
        const headerRow = ['Level', 'Slots'];
        if (column1Present && column2Present) {
            separatorRow.push('---');
        }
        const getCastingName = (classDetails: ClassDetails | null, includeLevel: boolean): string => {
            let className = classDetails?.classInfo?.name || '';
            if (className.startsWith("Inate Caster")) {
                return '';
            }
            if (className === 'Fighter' || className === 'Rogue') {
                className = classDetails?.archetype?.name || '';
            }
            if (includeLevel && classDetails?.classLevel != 21) {
                return `${className}-${classDetails?.classLevel}`
            }
            return className || '';
        }

        if (column1Present) headerRow.push(getCastingName(classDetails1, true) + ' Spells');
        if (column2Present) headerRow.push(getCastingName(classDetails2, true) + ' Spells');
        table.unshift(separatorRow);
        table.unshift(headerRow);

        let desc = '';
        for (const row of table) {
            desc += '| ' + row.join(' | ') + ' |\n';
        }
        desc += '\n\n\n';

        const nonWarlackPresent = !warlockPresent || (column1Present && column2Present);
        if (nonWarlackPresent) {
            desc += 'All expended spell slots recover after finishing a long rest.\n';
        }
        if (warlockPresent) {
            desc += '\\* All warlock spell slots recover after a long or short rest.\n';
        }

        const spellcastingAttribute1 = classDetails1 && ClassService.getSpellCastingAttribute(classDetails1);
        const spellcastingAttribute2 = classDetails2 && ClassService.getSpellCastingAttribute(classDetails2);
        let attr1 = spellcastingAttribute1?.substring(0, 3).toUpperCase() || '';
        const attr2 = spellcastingAttribute2?.substring(0, 3).toUpperCase() || '';
        let name1 = getCastingName(classDetails1, false);
        let name2 = getCastingName(classDetails2, false);
        if (column1Present && column2Present && (attr1 === attr2)) {
            // if both columns are spellcaster, but they use the same attribute,
            // combine their names into one, and treat it as if only one column exists at this point:
            name1 = name1 && name2 ? (name1 + '/' + name2) : name1 + name2;
            name2 = '';
            column2Present = false;
        }

        let totalLevels = SpellService.getTotalSpellLevel(classDetails1, classDetails2);

        // if (column1Present) {
        //   desc += SpellService.getSpellDc(name1, attr1);
        // }
        // if (column2Present) {
        //   desc += SpellService.getSpellDc(name2, attr2);
        // }

        if (!column1Present && column2Present) {
            column1Present = true;
            column2Present = false;
            name1 = name2;
            attr1 = attr2;
        }

        if (column1Present) {
            // case 1: single class: 
            // case 2: dual class, both using same attribute
            // case 3: dual class, each using different attributes
            desc += `\nAs a ${ClassService.getLevelString(totalLevels)}-level spellcaster,`;
            let fullAttr1 = Attributes.getAttrName(attr1) || attr1;
            fullAttr1 = fullAttr1.charAt(0).toUpperCase() + fullAttr1.substring(1);
            if (!column2Present || (attr1 === attr2)) {
                desc += ' its spellcasting ability is ' + fullAttr1;
                desc += SpellService.getSpellDcHit(name1, attr1);
            } else {
                desc += ' its spellcasting abilities are ' + attr1.toUpperCase() + ' and ' + attr2.toUpperCase();
                desc += SpellService.getSpellDcHit(name1, attr1);
                desc += SpellService.getSpellDcHit(name2, attr2);
            }
        }
        sections.push(new Section(`Prepared Spells`, desc));
    }

    // static getSpellDc = (name: string, attr: string): string => {
    //     let desc = (name) ? `\n**${name} ` : `\n> **`;
    //     desc += `Spell save DC** = [${attr} SAVE] (8 + prof + ${attr})`;
    //     desc += `\n> **attack modifier** = [${attr} ATK] (prof + ${attr})`;
    //     return desc;
    // }
    static getSpellDcHit = (name: string, attr: string): string => {
        return `\n(spell save DC [${attr} SAVE], [${attr} ATK] to hit with ${name} spell attacks)`;
    }
}
export default SpellService;